import React from "react";
import PropTypes from "prop-types";
import { Check, Loader2, X, Circle } from "lucide-react";
import { PIPELINE_STATES } from "../../services/LabService";
import { formatDuration } from "./labUi";

/**
 * 4-state stepper matching the Lab Step Function (backend note §4.6).
 * The backend returns `states: [{state, entered_at, exited_at, status}, ...]`
 * where `status` is "ok" | "failed" and a missing `exited_at` means the
 * state is still running.
 */
const normalizeState = (server) => {
    if (!server) return "pending";
    if (server.status === "failed") return "failed";
    if (server.exited_at) return "succeeded";
    if (server.entered_at) return "running";
    return "pending";
};

const statusToIcon = {
    succeeded: (key) => <Check key={key} className="w-4 h-4 text-white" />,
    running:   (key) => <Loader2 key={key} className="w-4 h-4 text-white animate-spin" />,
    failed:    (key) => <X key={key} className="w-4 h-4 text-white" />,
    pending:   (key) => <Circle key={key} className="w-3 h-3 text-slate-400" />,
};

const statusToCircle = {
    succeeded: "bg-emerald-500 border-emerald-500",
    running:   "bg-blue-500 border-blue-500",
    failed:    "bg-red-500 border-red-500",
    pending:   "bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700",
};

const statusToLine = {
    succeeded: "bg-emerald-500",
    running:   "bg-gradient-to-r from-emerald-500 to-blue-500",
    failed:    "bg-red-300",
    pending:   "bg-slate-200 dark:bg-slate-800",
};

const durationMs = (server) => {
    if (!server?.entered_at || !server?.exited_at) return null;
    return new Date(server.exited_at).getTime() - new Date(server.entered_at).getTime();
};

const PipelineTimeline = ({ states }) => {
    const byName = new Map((states || []).map((s) => [s.state || s.name, s]));
    const merged = PIPELINE_STATES.map((meta) => {
        const server = byName.get(meta.name) || null;
        return {
            ...meta,
            server,
            uiStatus: normalizeState(server),
            duration_ms: durationMs(server),
        };
    });

    return (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6">
            <div className="flex items-start">
                {merged.map((state, idx) => {
                    const isLast = idx === merged.length - 1;
                    const next = merged[idx + 1];
                    return (
                        <React.Fragment key={state.name}>
                            <div className="flex flex-col items-center flex-shrink-0" style={{ minWidth: 96 }}>
                                <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition ${statusToCircle[state.uiStatus]}`}>
                                    {statusToIcon[state.uiStatus](state.name)}
                                </div>
                                <div className="mt-2 text-xs font-medium text-center text-slate-700 dark:text-slate-200">
                                    {state.label}
                                </div>
                                <div className="text-[11px] text-slate-500 mt-0.5 text-center">
                                    {state.uiStatus === "succeeded" && state.duration_ms != null
                                        ? `✓ ${formatDuration(state.duration_ms)}`
                                        : state.uiStatus === "running"
                                            ? "running…"
                                            : state.uiStatus === "failed"
                                                ? "failed"
                                                : "⌛"}
                                </div>
                            </div>
                            {!isLast ? (
                                <div className="flex-1 h-[2px] mt-4 rounded-full overflow-hidden">
                                    <div
                                        className={`h-full ${
                                            next.uiStatus === "running" || next.uiStatus === "succeeded"
                                                ? statusToLine.succeeded
                                                : statusToLine[state.uiStatus]
                                        }`}
                                    />
                                </div>
                            ) : null}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
};

PipelineTimeline.propTypes = {
    states: PropTypes.arrayOf(PropTypes.shape({
        state: PropTypes.string,        // new contract
        name: PropTypes.string,         // legacy
        entered_at: PropTypes.string,
        exited_at: PropTypes.string,
        status: PropTypes.string,
    })),
};
PipelineTimeline.defaultProps = { states: [] };

export default PipelineTimeline;
